import type { Enemy, DotState } from "../entities/Enemy.ts";
import type { ContagionState } from "../entities/Enemy.ts";
import type { EventBus } from "../core/EventBus.ts";
import type { VirusSynergyMods } from "../entities/types.ts";
import { isStealthHidden } from "./StealthGuard.ts";

/**
 * Virus contagion spread system (B1b).
 *
 * An infected enemy periodically attempts to spread the virus DoT to nearby
 * uninfected enemies. Spread is:
 *   - generation-capped: infections at generation >= maxGenerations do not spread
 *   - seeded-RNG-gated: per-candidate probability via ctx.rng
 *   - snapshot-based: newly-infected enemies do NOT spread in the same tick
 *   - stealth-aware: isStealthHidden enemies are skipped (both as sources and targets)
 *   - idempotent: already-infected enemies are never re-infected
 */
export function updateContagionSpread(
  enemies: Enemy[],
  dt: number,
  ctx: {
    rng: () => number;
    bus: EventBus;
    virusMods?: VirusSynergyMods | null;
  },
): void {
  // Collect pass — determine which infections should spread this tick,
  // accumulate (target, sourceDot) pairs without mutating any target yet.
  const newInfections: Array<{ target: Enemy; from: DotState }> = [];

  for (const src of enemies) {
    if (!src.active) continue;
    if (isStealthHidden(src)) continue;

    const dot = src.dots.get("virus");
    if (!dot || !dot.contagion) continue;

    const c: ContagionState = dot.contagion;
    c.spreadTimer -= dt;
    if (c.spreadTimer > 0) continue;

    // Reset cadence
    c.spreadTimer = c.spreadInterval;

    const markedBypassesCap =
      (ctx.virusMods?.markedIgnoreGenerationCap ?? false) && src.marked;

    // Generation cap — infected but capped infections still tick & damage
    if (!markedBypassesCap && dot.generation >= c.maxGenerations) continue;

    const effectiveSpreadChance = markedBypassesCap
      ? (ctx.virusMods?.markedSpreadChance ?? 1)
      : c.spreadChance;

    const rSq = c.spreadRadius * c.spreadRadius;

    for (const tgt of enemies) {
      if (tgt === src) continue;
      if (!tgt.active) continue;
      if (isStealthHidden(tgt)) continue;
      if (tgt.dots.has("virus")) continue; // never re-infect

      const dx = tgt.x - src.x;
      const dy = tgt.y - src.y;
      if (dx * dx + dy * dy > rSq) continue;

      if (ctx.rng() < effectiveSpreadChance) {
        newInfections.push({ target: tgt, from: dot });
      }
    }
  }

  // Apply pass — snapshot: newly-infected enemies do NOT spread this tick
  for (const { target, from } of newInfections) {
    // Guard: two sources may have selected the same target in one pass
    if (target.dots.has("virus")) continue;

    const c = from.contagion!;
    target.applyDot(
      from.damage,
      c.duration,
      from.tickInterval,
      "virus",
      from.generation + 1,
      { ...c, spreadTimer: c.spreadInterval },
    );

    ctx.bus.emit("virus.spread", {
      x: target.x,
      y: target.y,
      generation: from.generation + 1,
    });
  }
}
